import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Calendar, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [leavingId, setLeavingId] = useState(null);

  const fetchBookings = () => {
    setLoading(true);
    Promise.all([api.get("/classes/bookings/me"), api.get("/classes/waitlist/me")])
      .then(([b, w]) => {
        setBookings(b.data);
        setWaitlist(w.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await api.post(`/classes/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  const handleLeaveWaitlist = async (sessionId) => {
    setLeavingId(sessionId);
    try {
      await api.delete(`/classes/${sessionId}/waitlist`);
      toast.success("Removed from the waitlist");
      fetchBookings();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLeavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-bookings-page">
      <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl mb-8">My Bookings</h1>
      {bookings.length === 0 ? (
        <p className="text-smoke">You haven't booked any classes yet.</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div
              key={b.id}
              className="bg-surface border border-line p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
              data-testid={`booking-row-${b.id}`}
            >
              <Calendar className="h-5 w-5 text-gold hidden sm:block" />
              <div className="flex-1">
                <p className="text-cream font-semibold">{b.class_type}</p>
                <p className="text-smoke text-sm">
                  {b.date} at {b.start_time}
                </p>
              </div>
              <span
                className={`text-xs uppercase tracking-widest px-3 py-1 border w-fit ${
                  b.status === "confirmed" ? "border-gold text-gold" : "border-line text-smoke"
                }`}
              >
                {b.status}
              </span>
              {b.status === "confirmed" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cancellingId === b.id}
                  onClick={() => handleCancel(b.id)}
                  className="rounded-none border-line text-smoke hover:border-red-400 hover:text-red-400 w-full sm:w-auto"
                  data-testid={`cancel-booking-button-${b.id}`}
                >
                  {cancellingId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="font-anton uppercase tracking-tighter text-cream text-2xl sm:text-3xl mt-12 mb-6">My Waitlists</h2>
      {waitlist.length === 0 ? (
        <p className="text-smoke" data-testid="waitlist-empty-message">
          You're not on any waitlists. When a class is full, hit "Join Waitlist" on the{" "}
          <Link to="/schedule" className="text-gold underline underline-offset-4">
            schedule
          </Link>{" "}
          and we'll email you the moment a spot opens.
        </p>
      ) : (
        <div className="space-y-3" data-testid="waitlist-list">
          {waitlist.map((w) => (
            <div
              key={w.id}
              className="bg-surface border border-line p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
              data-testid={`waitlist-row-${w.id}`}
            >
              <Hourglass className="h-5 w-5 text-gold hidden sm:block" />
              <div className="flex-1">
                <p className="text-cream font-semibold">{w.class_type}</p>
                <p className="text-smoke text-sm">
                  {w.date} at {w.start_time}
                </p>
              </div>
              <span
                className={`text-xs uppercase tracking-widest px-3 py-1 border w-fit ${
                  w.status === "notified" ? "border-gold text-gold" : "border-line text-smoke"
                }`}
                data-testid={`waitlist-status-${w.id}`}
              >
                {w.status === "notified" ? "Spot open — book now!" : "Waiting"}
              </span>
              {w.status === "notified" && (
                <Button
                  asChild
                  size="sm"
                  className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full sm:w-auto"
                  data-testid={`waitlist-book-link-${w.id}`}
                >
                  <Link to="/schedule">Book</Link>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={leavingId === w.session_id}
                onClick={() => handleLeaveWaitlist(w.session_id)}
                className="rounded-none border-line text-smoke hover:border-red-400 hover:text-red-400 w-full sm:w-auto"
                data-testid={`leave-waitlist-button-${w.id}`}
              >
                {leavingId === w.session_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Leave"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
